import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/material/styles';
import type { WorkspaceAgentTurn } from '../../types';
import useVoyagerStore from '../../stores/useVoyagerStore';
import { formatKpiValue } from '../agent-common/formatKpiValue';
import TraceLink from '../agent-common/TraceLink';
import ProposedPatchCard from './ProposedPatchCard';
import ClarificationCard from './ClarificationCard';

interface Props {
  turn: WorkspaceAgentTurn;
}

const WorkspaceTurn: React.FC<Props> = ({ turn }) => {
  const selectPerspective = useVoyagerStore((s) => s.selectPerspective);
  const send = useVoyagerStore((s) => s.sendWorkspaceAgentQuery);
  const suggested = turn.response?.suggested_perspective_id;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* User bubble */}
      <Box sx={{ alignSelf: 'flex-end', maxWidth: '85%' }}>
        <Paper
          variant="outlined"
          sx={(theme) => ({
            px: 1.25,
            py: 0.75,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            borderColor: alpha(theme.palette.primary.main, 0.35),
          })}
        >
          <Typography variant="body2" sx={{ fontSize: 13, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            {turn.userText}
          </Typography>
        </Paper>
      </Box>

      {/* Agent bubble */}
      <Box sx={{ alignSelf: 'flex-start', maxWidth: '95%', width: '100%' }}>
        {turn.status === 'running' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
            <CircularProgress size={14} />
            <Typography variant="caption">Thinking…</Typography>
          </Box>
        )}

        {turn.status === 'error' && (
          <Paper variant="outlined" sx={(theme) => ({ p: 1, borderColor: 'error.light', bgcolor: alpha(theme.palette.error.main, 0.08) })}>
            <Typography variant="caption" color="error">
              {turn.error || 'Agent error'}
            </Typography>
          </Paper>
        )}

        {turn.response && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {turn.response.explanation && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: turn.response.kind === 'summary' ? 14 : 13,
                  fontWeight: turn.response.kind === 'summary' ? 600 : 400,
                  lineHeight: 1.45,
                  whiteSpace: 'pre-wrap',
                  overflowWrap: 'anywhere',
                }}
              >
                {turn.response.explanation}
              </Typography>
            )}

            {turn.response.key_numbers && turn.response.key_numbers.length > 0 && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: turn.response.key_numbers.length === 1 ? '1fr' : '1fr 1fr',
                  gap: 1,
                }}
              >
                {turn.response.key_numbers.slice(0, 4).map((kn, i) => (
                  <Paper
                    key={i}
                    variant="outlined"
                    sx={{
                      p: 1.25,
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.25,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: 18, overflowWrap: 'anywhere' }}
                    >
                      {formatKpiValue(kn.value, kn.format)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.3, fontSize: 11 }}>
                      {kn.label}
                    </Typography>
                    {kn.note && (
                      <Typography variant="caption" sx={{ color: 'text.secondary', opacity: 0.8, fontSize: 10 }}>
                        {kn.note}
                      </Typography>
                    )}
                  </Paper>
                ))}
              </Box>
            )}

            {turn.response.highlights && turn.response.highlights.length > 0 && (
              <Paper variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
                <Typography variant="overline" sx={{ color: 'text.secondary', fontSize: 10 }}>
                  Highlights
                </Typography>
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                  {turn.response.highlights.map((h, i) => (
                    <Typography
                      key={i}
                      component="li"
                      variant="body2"
                      sx={{ fontSize: 13, overflowWrap: 'anywhere', mb: 0.25 }}
                    >
                      {h}
                    </Typography>
                  ))}
                </Box>
              </Paper>
            )}

            {turn.response.clarifying_questions && turn.response.clarifying_questions.length > 0 ? (
              <ClarificationCard turn={turn} />
            ) : turn.response.clarifying_question ? (
              <Paper variant="outlined" sx={(theme) => ({ p: 1, borderColor: 'warning.light', bgcolor: alpha(theme.palette.warning.main, 0.1) })}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                  Clarification needed
                </Typography>
                <Typography variant="body2" sx={{ fontSize: 13, mt: 0.25, overflowWrap: 'anywhere' }}>
                  {turn.response.clarifying_question}
                </Typography>
              </Paper>
            ) : null}

            {suggested && (
              <Paper variant="outlined" sx={{ p: 1 }}>
                <Typography variant="caption">
                  This looks like a question for perspective <b>{suggested}</b>.
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Button size="small" variant="outlined" onClick={() => selectPerspective(suggested)}>
                    Switch perspective
                  </Button>
                </Box>
              </Paper>
            )}

            {turn.response.patch && <ProposedPatchCard turn={turn} />}

            {turn.response.follow_ups && turn.response.follow_ups.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {turn.response.follow_ups.map((f) => (
                  <Chip
                    key={f}
                    label={f}
                    size="small"
                    variant="outlined"
                    onClick={() => send(f)}
                    sx={{ fontSize: 11, cursor: 'pointer' }}
                  />
                ))}
              </Box>
            )}

            {turn.response.session_id && (
              <Box sx={{ mt: 0.25, ml: -0.75 }}>
                <TraceLink sessionId={turn.response.session_id} />
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default WorkspaceTurn;
